import React from 'react'
import { useState, useEffect } from 'react'
import { getUserGameProgress, getTimeRemaining } from '../utils/gameUnlockService'
import PaymentModal from '../components/PaymentModal'
import ReferralCard from '../components/ReferralCard'
import './Menu.css'

export default function Menu({ user, onSelectGame, onLogout }) {
  const [userProgress, setUserProgress] = useState(null)
  const [showPayment, setShowPayment] = useState(false)
  const [gameToUnlock, setGameToUnlock] = useState(null)
  const [progressNotification, setProgressNotification] = useState(null)
  const previousProgressRef = React.useRef(null)

  // Garante que o scroll funciona no menu
  useEffect(() => {
    // Remove a classe game-active se existir
    document.body.classList.remove('game-active')
    
    // Força estilos corretos no body
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.width = ''
    document.body.style.height = ''
    
    return () => {
      // Cleanup
    }
  }, [])

  const loadUserProgress = React.useCallback(async () => {
    if (!user) return
    const progress = await getUserGameProgress(user.uid)
    
    // Verifica se há mudança no progresso
    if (previousProgressRef.current && progress) {
      const gameOrder = ['tetris', 'snake', 'pong', 'breakout']
      
      gameOrder.forEach(game => {
        const oldTime = previousProgressRef.current.playTime?.[game] || 0
        const newTime = progress.playTime?.[game] || 0
        
        if (newTime > oldTime && newTime < 7200) {
          const remaining = getTimeRemaining(newTime)
          const percentage = Math.min(100, (newTime / 7200) * 100)
          
          setProgressNotification({
            game,
            remaining,
            percentage: percentage.toFixed(0)
          })
          
          setTimeout(() => setProgressNotification(null), 5000)
        }
      })
    }
    
    previousProgressRef.current = progress
    setUserProgress(progress)
  }, [user])

  useEffect(() => {
    if (user) {
      loadUserProgress()
      
      // Atualiza o progresso a cada 10 segundos
      const interval = setInterval(() => {
        loadUserProgress()
      }, 10000)
      
      return () => clearInterval(interval)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  function handleGameClick(game) {
    // Verifica se é um jogo desktop-only e está em mobile
    if (game.desktopOnly) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
      const isSmallScreen = window.innerWidth < 768
      
      if (isMobile && isSmallScreen) {
        alert('⚠️ San Code está disponível apenas para Desktop e Tablet!\n\n🖥️ Por favor, acesse de um computador ou tablet para jogar.\n\n📱 Este jogo requer teclado e tela maior para melhor experiência.')
        return
      }
    }
    
    const unlockedGames = userProgress?.unlockedGames || ['tetris']
    
    // Verifica se é um jogo com preview (3 primeiros níveis grátis)
    if (game.hasPreview && !game.fullUnlocked) {
      // Permite jogar mas com limitação de 3 níveis
      onSelectGame(game.id)
    } else if (unlockedGames.includes(game.id) || game.unlocked) {
      onSelectGame(game.id)
    } else {
      setGameToUnlock(game.id)
      setShowPayment(true)
    }
  }

  function handlePaymentComplete(paymentData) {
    // Atualiza o progresso do usuário
    loadUserProgress()
  }

  const games8Bits = [
    {
      id: 'tetris',
      name: 'TETRIS',
      description: 'O clássico jogo de blocos que conquistou gerações',
      preview: '🟦',
      color: '#00eaff',
      unlocked: true,
      fullUnlocked: true,  // Tetris sempre 100% liberado
      hasPreview: false,   // Sem preview, já é full
      era: '8-bits'
    },
    {
      id: 'snake',
      name: 'SNAKE',
      description: 'A cobrinha que todos amam',
      preview: '🐍',
      color: '#00f000',
      unlocked: userProgress?.unlockedGames?.includes('snake'),
      hasPreview: true,
      fullUnlocked: userProgress?.unlockedGames?.includes('snake'),
      era: '8-bits'
    },
    {
      id: 'pong',
      name: 'PONG',
      description: 'O primeiro jogo de arcade da história',
      preview: '🏓',
      color: '#f0f000',
      unlocked: userProgress?.unlockedGames?.includes('pong'),
      hasPreview: true,
      fullUnlocked: userProgress?.unlockedGames?.includes('pong'),
      era: '8-bits'
    },
    {
      id: 'breakout',
      name: 'BREAKOUT',
      description: 'Quebre todos os blocos com a bola',
      preview: '🧱',
      color: '#f0a000',
      unlocked: userProgress?.unlockedGames?.includes('breakout'),
      hasPreview: true,
      fullUnlocked: userProgress?.unlockedGames?.includes('breakout'),
      era: '8-bits'
    }
  ]

  const games16Bits = [
    {
      id: 'sancode',
      name: 'SAN CODE',
      description: '🖥️ Aventura platformer no mundo da TI - Desktop/Tablet apenas',
      preview: '👾',
      color: '#00d4ff',
      unlocked: userProgress?.unlockedGames?.includes('sancode'),
      hasPreview: true,
      fullUnlocked: userProgress?.unlockedGames?.includes('sancode'),
      era: '16-bits',
      desktopOnly: true
    },
    {
      id: 'codeinbug',
      name: 'CODE IN BUG',
      description: '☕ Estilo Pac-Man: corrija bugs e fuja dos pontos e vírgulas!',
      preview: '☕',
      color: '#ff6b35',
      unlocked: userProgress?.unlockedGames?.includes('codeinbug'),
      hasPreview: true,
      fullUnlocked: userProgress?.unlockedGames?.includes('codeinbug'),
      era: '16-bits'
    },
    {
      id: 'killerdeveloper',
      name: 'KILLER DEVELOPER',
      description: '💻🔫 Stealth top-down: elimine alvos sem ser visto!',
      preview: '💻',
      color: '#00ff00',
      unlocked: true,
      hasPreview: false,
      fullUnlocked: true,
      era: '16-bits'
    }
  ]

  const allGames = [...games8Bits, ...games16Bits]
  const gameOrder = ['tetris', 'snake', 'pong', 'breakout', 'sancode', 'codeinbug']
  
  function getUnlockInfo(game) {
    if (game.unlocked) return null
    
    const gameIndex = gameOrder.indexOf(game.id)
    const previousGame = gameOrder[gameIndex - 1]
    const playTime = userProgress?.playTime?.[previousGame] || 0
    const timeRemaining = getTimeRemaining(playTime)
    
    return {
      timeRemaining,
      progress: Math.min(100, (playTime / 7200) * 100)
    }
  }

  return (
    <div className="menu-container">
      {progressNotification && (
        <div className="progress-notification">
          <div className="notification-content">
            <span className="notification-icon">🎮</span>
            <div className="notification-text">
              <strong>Progresso Atualizado!</strong>
              <p>Continue jogando {progressNotification.game.toUpperCase()}</p>
              <p className="notification-detail">
                {progressNotification.percentage}% completo • {progressNotification.remaining} restantes
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="menu-header">
        <div className="user-section">
          {user?.photoURL ? (
            <img src={user.photoURL} alt="User" className="menu-user-photo" />
          ) : (
            <div className="menu-user-photo menu-user-placeholder">
              {(user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="user-details">
            <span className="user-greeting">Bem-vindo,</span>
            <span className="user-menu-name">{user?.displayName || user?.email || 'Jogador'}</span>
          </div>
        </div>
        
        <button className="logout-btn" onClick={onLogout}>
          🚪 Sair
        </button>
      </div>

      <div className="menu-content">
        <div className="menu-title-section">
          <img 
            src="/logo.png" 
            alt="San Dev Code Logo" 
            className="menu-logo"
          />
          <h1 className="menu-main-title">
            SAN DEV CODE
          </h1>
          <h2 className="menu-subtitle">RETRO GAMES</h2>
          <p className="menu-tagline">Reviva os clássicos da era 8-bits e 16-bits</p>
        </div>

        {/* Jogos 8-bits */}
        <div className="games-section">
          <h3 className="section-title">
            <span className="section-icon">🕹️</span>
            ERA 8-BITS
            <span className="section-subtitle">Clássicos Atemporais</span>
          </h3>
          <div className="games-grid">
            {games8Bits.map((game) => renderGameCard(game))}
          </div>
        </div>

        {/* Jogos 16-bits */}
        <div className="games-section">
          <h3 className="section-title">
            <span className="section-icon">🎮</span>
            ERA 16-BITS
            <span className="section-subtitle">Aventuras Épicas</span>
          </h3>
          <div className="games-grid">
            {games16Bits.map((game) => renderGameCard(game))}
          </div>
        </div>

        <ReferralCard user={user} />

        <div className="menu-footer">
          <p className="footer-text">
            Desenvolvido por <a href="https://github.com/jusansilva" target="_blank" rel="noopener noreferrer">San Dev Code</a>
          </p>
          <p className="footer-version">v1.0.0 • 2024</p>
        </div>
      </div>

      <PaymentModal
        show={showPayment}
        onClose={() => setShowPayment(false)}
        onPaymentComplete={handlePaymentComplete}
        gameToUnlock={gameToUnlock}
        userProgress={userProgress}
        user={user}
      />
    </div>
  )

  function renderGameCard(game) {
    return (
      <div 
        key={game.id} 
        className={`game-card ${!game.fullUnlocked && !game.hasPreview ? 'locked' : ''}`}
        style={{ borderColor: game.color }}
        onClick={() => handleGameClick(game)}
      >
        <div className="game-preview" style={{ color: game.color }}>
          <span className="game-icon">{game.preview}</span>
          {game.id === 'tetris' && (
            <div className="tetris-preview">
              <div className="preview-block" style={{ backgroundColor: game.color }}></div>
              <div className="preview-block" style={{ backgroundColor: game.color }}></div>
              <div className="preview-block" style={{ backgroundColor: game.color }}></div>
              <div className="preview-block" style={{ backgroundColor: game.color }}></div>
            </div>
          )}
          {!game.fullUnlocked && !game.unlocked && <div className="lock-overlay">🔒</div>}
        </div>
        
        <div className="game-info">
          <h3 className="game-title" style={{ color: game.color }}>{game.name}</h3>
          <p className="game-description">{game.description}</p>
          
          {game.desktopOnly && (
            <div className="desktop-only-badge">
              🖥️ Desktop/Tablet
            </div>
          )}
          
          {game.hasPreview && !game.fullUnlocked && (
            <div className="preview-badge">
              🎁 PREVIEW: 3 Níveis Grátis
            </div>
          )}
          
          {game.fullUnlocked || game.unlocked ? (
            <div className="game-status available">
              ▶ JOGAR AGORA
            </div>
          ) : game.hasPreview ? (
            <div className="game-status preview">
              🎮 JOGAR PREVIEW
            </div>
          ) : (
            <>
              <div className="game-status locked">
                🔒 BLOQUEADO
              </div>
              {(() => {
                const unlockInfo = getUnlockInfo(game)
                if (unlockInfo) {
                  return (
                    <div className="unlock-progress">
                      <div className="time-info">
                        <span className="time-label">⏱️ Tempo de Jogo:</span>
                        <span className="time-value">{unlockInfo.timeRemaining}</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{ width: `${unlockInfo.progress}%`, backgroundColor: game.color }}
                        ></div>
                      </div>
                      <div className="unlock-options">
                        <small>🎮 Continue jogando para desbloquear</small>
                        <small className="or-divider">OU</small>
                        <small className="payment-option">💰 Pague R$ 10,00 agora</small>
                      </div>
                    </div>
                  )
                }
              })()}
            </>
          )}
        </div>
      </div>
    )
  }
}
